import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Save, ArrowLeft, AlertCircle, FilePlus } from "lucide-react";
import Select from "react-select";

function CreateInvoice() {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        invoice_number: "",
        date: "",
        due_date: "",
        subtotal: "",
        iva: "",
        freight: "",
        retention: "",
        rete_ica: "",
        extra_concept: "",
        extra_value: "",
        total: "",
        balance: "",
        supplier_id: "",
        observacion: "",
        discount: "",
        glosa: "",
        glosa_retencion: "",
        glosa_reteica: "",
        glosa_total: 0,
    });

    const supplierOptions = suppliers.map((s) => ({
        value: s.id,
        label: s.name,
    }));

    const showGlosaExtras = Number(form.glosa || 0) > 0;

    useEffect(() => {
        fetchSuppliers();
    }, []);

    const fetchSuppliers = async () => {
        try {
            const res = await api.get("/suppliers");
            setSuppliers(res.data);
        } catch {
            toast.error("Error al cargar proveedores");
        }
    };

    function formatNumberWithDots(value) {
        if (!value || value === "0") return "";
        return parseInt(value, 10).toLocaleString("es-CO");
    }
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Limpiar error al escribir
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        let newValue = value.replace(/\./g, "");

        if (["subtotal", "iva", "freight", "retention", "rete_ica", "discount", "glosa", "glosa_retencion", "glosa_reteica", "extra_value"].includes(name)) {
            newValue = newValue.replace(/[^0-9]/g, "");
        }

        const updatedForm = { ...form, [name]: newValue };

        // 🔹 Convertimos todos los valores a número para el cálculo visual
        const subtotal = parseFloat(updatedForm.subtotal || 0);
        const iva = parseFloat(updatedForm.iva || 0);
        const freight = parseFloat(updatedForm.freight || 0);
        const extraValue = parseFloat(updatedForm.extra_value || 0);
        const retention = parseFloat(updatedForm.retention || 0);
        const rete_ica = parseFloat(updatedForm.rete_ica || 0);
        const discount = parseFloat(updatedForm.discount || 0);
        const glosa = parseFloat(updatedForm.glosa || 0);
        const glosaRetencion = parseFloat(updatedForm.glosa_retencion || 0);
        const glosaReteica = parseFloat(updatedForm.glosa_reteica || 0);

        const totalFactura = subtotal + iva + freight + extraValue;
        let glosaTotal = Math.max(0, glosa - glosaRetencion - glosaReteica);

        if (glosa <= 0) {
            updatedForm.glosa_retencion = "";
            updatedForm.glosa_reteica = "";
            glosaTotal = 0;
        }

        // 🔹 RESTAMOS TODAS LAS DEDUCCIONES (Incluyendo descuento y glosa total)
        const valorAPagar = totalFactura - retention - rete_ica - discount - glosaTotal;

        updatedForm.total = totalFactura;
        updatedForm.balance = valorAPagar;
        updatedForm.glosa_total = glosaTotal;

        setForm(updatedForm);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let newErrors = {};

        const today = new Date().toISOString().split('T');
        // 1. Validaciones de presencia
        if (!form.invoice_number.trim()) {
            newErrors.invoice_number = "El número de factura es obligatorio";
        }
        if (!form.supplier_id) {
            newErrors.supplier_id = "Debe seleccionar un proveedor";
        }
        if (!form.date) {
            newErrors.date = "La fecha es obligatoria";
        } else if (form.date > today) {
            // 🔹 BLOQUEO DE FECHA FUTURA
            newErrors.date = "La fecha no puede ser mayor a la de hoy";
            toast.error("No se permiten facturas con fecha futura");
        }
        if (!form.due_date) {
            newErrors.due_date = "La fecha de vencimiento es obligatoria";
        }
        if (form.date && form.due_date && form.due_date < form.date) {
            newErrors.due_date = "El vencimiento no puede ser anterior a la emisión";
        }

        // 2. Validaciones numéricas individuales
        const fieldsToValidate = ["subtotal", "iva", "freight", "retention", "rete_ica", "discount", "glosa", "glosa_retencion", "glosa_reteica", "extra_value"];

        fieldsToValidate.forEach((field) => {
            const value = form[field];
            if (field === "subtotal") {
                if (!value || isNaN(value) || parseFloat(value) <= 0) {
                    newErrors.subtotal = "El subtotal debe ser mayor a 0";
                }
            } else {
                // Solo validar si el usuario escribió algo
                if (value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
                    newErrors[field] = "Debe ingresar un número válido";
                }
            }
        });

        const glosaValue = parseFloat(form.glosa || 0);
        const glosaRetencionValue = parseFloat(form.glosa_retencion || 0);
        const glosaReteicaValue = parseFloat(form.glosa_reteica || 0);

        if (glosaRetencionValue > glosaValue) {
            newErrors.glosa_retencion = "No puede exceder el valor de la glosa";
        }
        if (glosaReteicaValue > glosaValue) {
            newErrors.glosa_reteica = "No puede exceder el valor de la glosa";
        }
        if (glosaRetencionValue + glosaReteicaValue > glosaValue) {
            newErrors.glosa = "La suma de glosa retención y glosa reteica no puede ser mayor a la glosa";
        }

        // 3. 🔹 Lógica para evitar Saldo Negativo
        const subtotal = parseFloat(form.subtotal || 0);
        const iva = parseFloat(form.iva || 0);
        const freight = parseFloat(form.freight || 0);
        const extraValue = parseFloat(form.extra_value || 0);

        const totalBruto =
            subtotal +
            iva +
            freight +
            extraValue;
        const retention = parseFloat(form.retention || 0);
        const rete_ica = parseFloat(form.rete_ica || 0);
        const discount = parseFloat(form.discount || 0);
        const glosa = parseFloat(form.glosa || 0);
        const glosaRetencion = parseFloat(form.glosa_retencion || 0);
        const glosaReteica = parseFloat(form.glosa_reteica || 0);
        const glosaTotal = Math.max(0, glosa - glosaRetencion - glosaReteica);
        const totalDeducciones = retention + rete_ica + discount + glosaTotal;

        // Si las deducciones superan lo que se va a cobrar
        if (totalDeducciones > totalBruto) {
            newErrors.balance = "Las deducciones superan el total de la factura";
            toast.error("El saldo a pagar no puede ser negativo");
        }

        // 4. Envío de errores o Payload
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // Si no es el error de balance (que ya tiene toast), avisar en general
            if (!newErrors.balance) toast.error("Revisa los campos obligatorios");
            return;
        }

        setErrors({});
        try {
            const payload = {
                supplier_id: form.supplier_id,
                invoice_number: form.invoice_number,
                date: form.date,
                due_date: form.due_date,
                subtotal,
                iva,
                freight,
                extra_concept: form.extra_concept?.trim() || null,
                extra_value: Number(form.extra_value || 0),
                retention,
                rete_ica,
                discount,
                glosa,
                glosa_retencion: Number(form.glosa_retencion || 0),
                glosa_reteica: Number(form.glosa_reteica || 0),
                glosa_total: Number(form.glosa_total || 0),
                observacion: form.observacion?.trim() || null,
            };

            await api.post("/invoices", payload);
            toast.success("Factura creada exitosamente");
            navigate("/invoices");
        } catch (error) {
            console.error("Error backend:", error.response?.data);
            toast.error(error.response?.data?.details || "Error al crear la factura");
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md max-w-4xl mx-auto">
            {/* Cabecera */}
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={() => navigate("/invoices")}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition"
                >
                    <ArrowLeft size={20} /> Volver
                </button>
            </div>

            <h2 className="text-3xl font-bold text-gray-800 mb-6">Crear Nueva Factura</h2>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Número de factura - Estilo Destacado */}
                <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <label className="block text-sm font-bold text-blue-900 mb-1 uppercase tracking-wider">
                        Número de Factura (Referencia)
                    </label>
                    <input
                        type="text"
                        name="invoice_number"
                        value={form.invoice_number}
                        onChange={handleChange}
                        placeholder="Ej: FAC-001"
                        className={`w-full border-2 px-4 py-3 rounded-lg text-lg font-mono outline-none transition-all shadow-sm ${errors.invoice_number
                            ? "border-red-500 focus:border-red-600"
                            : "border-blue-200 focus:border-blue-500"
                            }`}
                    />
                    {errors.invoice_number && (
                        <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <AlertCircle size={16} /> {errors.invoice_number}
                        </p>
                    )}
                </div>

                {/* Proveedor - Usando React Select con estilo */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
                    <Select
                        options={supplierOptions}
                        placeholder="-Seleccionar proveedor..."
                        value={supplierOptions.find((o) => o.value === form.supplier_id) || null}
                        onChange={(option) => {
                            if (errors.supplier_id) setErrors(prev => ({ ...prev, supplier_id: null }));
                            setForm({ ...form, supplier_id: option?.value || "" });
                        }}
                        classNamePrefix="react-select"
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderRadius: '0.5rem',
                                borderColor: errors.supplier_id ? '#ef4444' : '#d1d5db',
                                padding: '2px'
                            })
                        }}
                    />
                    {errors.supplier_id && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle size={14} /> {errors.supplier_id}
                        </p>
                    )}
                </div>

                {/* Fechas */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Emisión</label>
                    <input
                        type="date"
                        name="date"
                        value={form.date}
                        onChange={handleChange}
                        className={`w-full border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${errors.date ? "border-red-500" : "border-gray-300"
                            }`}
                    />
                    {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Vencimiento</label>
                    <input
                        type="date"
                        name="due_date"
                        value={form.due_date}
                        onChange={handleChange}
                        className={`w-full border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${errors.due_date ? "border-red-500" : "border-gray-300"
                            }`}
                    />
                    {errors.due_date && <p className="text-red-500 text-xs mt-1">{errors.due_date}</p>}
                </div>

                {/* Bloque de Valores Dinámicos (Solo campos estándar) */}
                {[
                    ["subtotal", "Subtotal"],
                    ["iva", "IVA"],
                    ["freight", "Flete"],
                    ["retention", "Retención"],
                    ["rete_ica", "Rete ICA"],
                    ["discount", "Descuento"],
                ].map(([field, label]) => (
                    <div key={field}>
                        <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
                        <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-400">$</span>
                            <input
                                type="text"
                                name={field}
                                value={formatNumberWithDots(form[field])}
                                onChange={handleChange}
                                className={`w-full border pl-7 pr-4 py-2 rounded-lg font-medium focus:ring-2 focus:ring-blue-500 outline-none ${errors[field] ? "border-red-500" : "border-gray-300"
                                    } bg-white text-gray-900`}
                            />
                        </div>
                        {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
                    </div>
                ))}

                {/* Concepto Adicional */}
                <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">
                                Concepto
                            </label>
                            <input
                                type="text"
                                name="extra_concept"
                                value={form.extra_concept}
                                onChange={handleChange}
                                placeholder="Ej: Ajuste, Recargo, Transporte..."
                                className="w-full border border-gray-300 px-4 py-2 rounded-lg font-medium focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                            />
                        </div>

                        {/* Valor Adicional */}
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">
                                Valor Adicional
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-400">$</span>
                                <input
                                    type="text"
                                    name="extra_value"
                                    value={formatNumberWithDots(form.extra_value)}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg font-medium focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* RENDERIZADO CONDICIONAL DE GLOSA */}
                {!showGlosaExtras ? (
                    /* MODO NORMAL: Se ve igual a los demás campos */
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Glosa</label>
                        <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-400">$</span>
                            <input
                                type="text"
                                name="glosa"
                                value={formatNumberWithDots(form.glosa)}
                                onChange={handleChange}
                                className={`w-full border pl-7 pr-4 py-2 rounded-lg font-medium focus:ring-2 focus:ring-blue-500 outline-none ${errors.glosa ? "border-red-500" : "border-gray-300"
                                    } bg-white text-gray-900`}
                            />
                        </div>
                        {errors.glosa && <p className="text-red-500 text-xs mt-1">{errors.glosa}</p>}
                    </div>
                ) : (
                    /* MODO DETALLE: Cuadro gris expandido con las 4 columnas */
                    <div className="md:col-span-2 bg-gray-50 p-5 rounded-xl border border-gray-200 shadow-sm animate-in fade-in duration-300">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-2 h-4 bg-blue-500 rounded-full"></div>
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Detalle de Glosas</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                            {/* 1. Glosa Base */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-600 mb-1">Glosa Base</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-400">$</span>
                                    <input
                                        type="text"
                                        name="glosa"
                                        value={formatNumberWithDots(form.glosa)}
                                        onChange={handleChange}
                                        className={`w-full border pl-7 pr-4 py-2 rounded-lg font-medium focus:ring-2 focus:ring-blue-500 outline-none ${errors.glosa ? "border-red-500" : "border-gray-300"
                                            } bg-white`}
                                    />
                                </div>
                            </div>

                            {/* 2. Glosa Retención */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-600 mb-1">G. Retención</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-400">$</span>
                                    <input
                                        type="text"
                                        name="glosa_retencion"
                                        value={formatNumberWithDots(form.glosa_retencion)}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-white text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            </div>

                            {/* 3. Glosa Rete ICA */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-600 mb-1">G. Rete ICA</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-400">$</span>
                                    <input
                                        type="text"
                                        name="glosa_reteica"
                                        value={formatNumberWithDots(form.glosa_reteica)}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-white text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            </div>

                            {/* 4. Glosa Total */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-600 mb-1">Glosa Total</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-400">$</span>
                                    <input
                                        type="text"
                                        name="glosa_total"
                                        value={formatNumberWithDots(form.glosa_total)}
                                        readOnly
                                        className="w-full border border-gray-300 pl-7 pr-4 py-2 rounded-lg bg-gray-200 text-gray-800 font-bold outline-none cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Resultado Final (Saldo) */}
                <div className={`p-4 rounded-lg shadow-inner flex flex-col justify-center transition-colors ${form.balance < 0 ? "bg-red-900" : "bg-gray-800"
                    }`}>
                    <label className="block text-xs font-bold uppercase opacity-70 mb-1 text-white">
                        Total a Pagar (Saldo)
                    </label>
                    <div className={`text-2xl font-bold ${form.balance < 0 ? "text-red-200" : "text-green-400"}`}>
                        $ {formatNumberWithDots(form.balance) || "0"}
                    </div>
                    {form.balance < 0 && (
                        <span className="text-[10px] text-red-300 mt-1 font-bold">⚠️ DEDUCCIONES EXCEDEN EL TOTAL</span>
                    )}
                </div>

                {/* Observaciones */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones Internas</label>
                    <textarea
                        name="observacion"
                        value={form.observacion}
                        onChange={handleChange}
                        rows={2}
                        placeholder="Información adicional sobre esta factura..."
                        className="w-full border border-gray-300 px-4 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>

                {/* Botón de Acción */}
                <div className="md:col-span-2 pt-4 border-t">
                    <button
                        type="submit"
                        className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-lg shadow-lg text-white bg-blue-600 hover:bg-blue-700 transition-all font-bold"
                    >
                        <Save size={20} />
                        Guardar Factura
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CreateInvoice;